import { ReactNode, useEffect, useState, useContext } from "react";
import Modal from 'react-modal';
import { XCircle } from "@phosphor-icons/react";
import './application-modal.scss';
import ButtonKMQ from "../../Button/ButtonKMQ";
import { ButtonTypes } from "../../../../constants/ui/types/ButtonTypes";
import { useNavigate } from "react-router-dom";
import { PortfolioContext } from "../../../../hooks/Context/PortfolioContext";
import { UserContext } from "../../../../hooks/Context/UserContext";

interface ApplicationModalProps {
  open: boolean,
  onClose: () => void,
}

const ApplicationModal = (props: ApplicationModalProps) => {
  const {
    open,
    onClose
  } = props;

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [portfolioState, setPortfolio] = useContext(PortfolioContext);
  const [user] = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const rootElement = document.getElementById('root');
      if (rootElement) {
        Modal.setAppElement('#root');
      } else {
        Modal.setAppElement('body');
      }
    } catch (error) {
      console.error('Failed to set app element for react-modal:', error);
    }
  }, []);

  const handleUseCurrentResume = () => {
    if (user.isSoft) {
      navigate('/login/sign-up');
    } else {
      navigate('/cpl-applicant');
    }
    onClose();
  };

  const handleUploadResume = () => {
    setPortfolio({ ...portfolioState, start_over: true });
    navigate("/cpl-applicant");
    onClose();
  };

  const handleProceed = () => {
    if (selectedOption === 'current') {
      handleUseCurrentResume();
    } else if (selectedOption === 'upload') {
      handleUploadResume();
    }
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      portalClassName={'application-modal'}
      contentLabel="Application Modal"
      ariaHideApp={true}
    >
      <div className={'application-modal-body'}>
        <span className={'application-modal-body-close'} onClick={ () => {
            setSelectedOption(null);
            onClose(); 
          }}
        >
          <XCircle size={28} color="#212121" />
        </span>
        <div className={'application-modal-body-title'}>
          Start new application
        </div>
        <div className={'application-modal-body-text'}>
          The changes here will not affect any previously submitted applications.
        </div>
        <div className={'application-modal-body-options'}>
          <div 
            className={`option-card ${selectedOption === 'current' ? 'selected' : ''}`}
            onClick={() => setSelectedOption('current')}
          >
            <div className="option-icon">
                <svg width="56" height="56" viewBox="0 0 50 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.92195 63.9279H43.8034C47.0677 63.9279 49.7223 61.2732 49.7223 58.0089V10.6571H49.7105C49.7105 10.3493 49.601 10.0475 49.3731 9.81957L39.9028 0.349218C39.6778 0.124293 39.373 0.0118379 39.0652 0.0118379V0H5.91897C2.65465 0 0 2.65465 0 5.91897V58.0059C0 61.2702 2.65465 63.9249 5.91897 63.9249L5.92195 63.9279ZM40.2516 4.04569L45.6793 9.47341H40.9619C40.5712 9.47341 40.2516 9.15378 40.2516 8.76313V4.04569ZM2.37019 5.92203C2.37019 3.96288 3.96242 2.37065 5.92157 2.37065H37.8844V8.76313C37.8844 10.4619 39.2665 11.841 40.9623 11.841H47.3547V58.0093C47.3547 59.9685 45.7625 61.5607 43.8034 61.5607H5.92195C3.9628 61.5607 2.37057 59.9685 2.37057 58.0093L2.37019 5.92203Z" fill="black"/>
                    <path d="M16.5312 41.531C16.5312 42.1589 16.955 42.6674 17.4783 42.6674H38.3131C38.8363 42.6674 39.2601 42.1589 39.2601 41.531C39.2601 40.9031 38.8363 40.3945 38.3131 40.3945H17.4783C16.955 40.3945 16.5312 40.9031 16.5312 41.531Z" fill="black"/>
                    <path d="M11.4119 42.6674H13.3059C13.8292 42.6674 14.253 42.1589 14.253 41.531C14.253 40.9031 13.8292 40.3945 13.3059 40.3945H11.4119C10.8886 40.3945 10.4648 40.9031 10.4648 41.531C10.4648 42.1589 10.8886 42.6674 11.4119 42.6674Z" fill="black"/>
                    <path d="M38.3131 46.4531H17.4783C16.955 46.4531 16.5312 46.9617 16.5312 47.5896C16.5312 48.2175 16.955 48.726 17.4783 48.726H38.3131C38.8363 48.726 39.2601 48.2175 39.2601 47.5896C39.2601 46.9617 38.8363 46.4531 38.3131 46.4531Z" fill="black"/>
                    <path d="M11.4119 48.726H13.3059C13.8292 48.726 14.253 48.2175 14.253 47.5896C14.253 46.9617 13.8292 46.4531 13.3059 46.4531H11.4119C10.8886 46.4531 10.4648 46.9617 10.4648 47.5896C10.4648 48.2175 10.8886 48.726 11.4119 48.726Z" fill="black"/>
                    <path d="M38.3131 52.5156H17.4783C16.955 52.5156 16.5312 53.0242 16.5312 53.6521C16.5312 54.28 16.955 54.7885 17.4783 54.7885H38.3131C38.8363 54.7885 39.2601 54.28 39.2601 53.6521C39.2601 53.0242 38.8363 52.5156 38.3131 52.5156Z" fill="black"/>
                    <path d="M11.4119 54.7885H13.3059C13.8292 54.7885 14.253 54.28 14.253 53.6521C14.253 53.0242 13.8292 52.5156 13.3059 52.5156H11.4119C10.8886 52.5156 10.4648 53.0242 10.4648 53.6521C10.4648 54.28 10.8886 54.7885 11.4119 54.7885Z" fill="black"/>
                    <path d="M24.862 31.9661C32.0417 31.9661 37.8841 26.1241 37.8841 18.944C37.8841 11.7639 32.0421 5.92188 24.862 5.92188C17.6818 5.92188 11.8398 11.7639 11.8398 18.944C11.8398 26.1241 17.6818 31.9661 24.862 31.9661ZM17.7977 26.8965C19.2064 24.3307 21.8877 22.6999 24.862 22.6999C27.8363 22.6999 30.5205 24.3306 31.9262 26.8965C30.044 28.5716 27.5728 29.5985 24.862 29.5985C22.1511 29.5985 19.6799 28.5716 17.7977 26.8965ZM20.8993 16.3693C20.8993 14.1852 22.6779 12.4065 24.862 12.4065C27.0461 12.4065 28.8248 14.1852 28.8248 16.3693C28.8248 18.5534 27.0461 20.332 24.862 20.332C22.6779 20.332 20.8993 18.5534 20.8993 16.3693ZM24.862 8.28992C30.7366 8.28992 35.5158 13.0695 35.5158 18.9437C35.5158 21.2166 34.7937 23.3178 33.5803 25.0491C32.4379 23.3089 30.8191 21.9772 28.9339 21.1781C30.3042 20.015 31.195 18.3044 31.195 16.3718C31.195 12.8796 28.3539 10.0415 24.8646 10.0415C21.3724 10.0415 18.5342 12.8826 18.5342 16.3718C18.5342 18.3044 19.4251 20.0179 20.7953 21.1781C18.9101 21.9772 17.2883 23.3089 16.1489 25.0491C14.9326 23.3178 14.2134 21.2136 14.2134 18.9437C14.2134 13.0691 18.993 8.28992 24.8672 8.28992H24.862Z" fill="black"/>
                </svg>
            </div>
            <div className="option-text">Use the current resume</div>
          </div>
          
          <div 
            className={`option-card ${selectedOption === 'upload' ? 'selected' : ''}`}
            onClick={() => setSelectedOption('upload')}
          >
            <div className="option-icon">
            <svg width="56" height="56" viewBox="0 0 48 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M48 21.9983C48.0005 27.7827 45.7074 33.3345 41.6169 37.4522C37.5264 41.5699 31.9677 43.9222 26.1438 43.9999H25.8462C20.1882 44.0139 14.7422 41.8636 10.6385 37.9953C10.4622 37.8298 10.3206 37.6314 10.2215 37.4115C10.1225 37.1916 10.0681 36.9545 10.0613 36.7137C10.0546 36.4729 10.0957 36.2331 10.1822 36.0081C10.2688 35.783 10.3991 35.5771 10.5658 35.4021C10.7324 35.2271 10.9322 35.0864 11.1536 34.9881C11.3751 34.8897 11.6138 34.8357 11.8563 34.829C12.0988 34.8222 12.3402 34.863 12.5668 34.949C12.7934 35.035 13.0007 35.1644 13.1769 35.3299C15.8165 37.8021 19.131 39.4471 22.7064 40.0593C26.2818 40.6716 29.9596 40.224 33.2804 38.7725C36.6011 37.3209 39.4176 34.9298 41.378 31.8978C43.3384 28.8659 44.3557 25.3274 44.3029 21.7246C44.2501 18.1217 43.1296 14.6141 41.0812 11.6401C39.0329 8.66602 36.1475 6.3573 32.7856 5.00234C29.4238 3.64737 25.7344 3.30623 22.1784 4.02154C18.6224 4.73684 15.3575 6.47688 12.7915 9.02427C12.7727 9.04452 12.7527 9.06366 12.7315 9.08157L6.59769 14.6645H12.9231C13.4127 14.6645 13.8823 14.8576 14.2285 15.2015C14.5747 15.5453 14.7692 16.0117 14.7692 16.4979C14.7692 16.9842 14.5747 17.4506 14.2285 17.7944C13.8823 18.1382 13.4127 18.3314 12.9231 18.3314H1.84615C1.35652 18.3314 0.886947 18.1382 0.540726 17.7944C0.194505 17.4506 0 16.9842 0 16.4979V5.49714C0 5.01088 0.194505 4.54453 0.540726 4.20069C0.886947 3.85685 1.35652 3.66368 1.84615 3.66368C2.33578 3.66368 2.80536 3.85685 3.15158 4.20069C3.4978 4.54453 3.69231 5.01088 3.69231 5.49714V12.3268L10.2115 6.41388C13.313 3.34566 17.2603 1.25878 21.5551 0.41668C25.8498 -0.425424 30.2995 0.0150024 34.3425 1.68236C38.3854 3.34972 41.8403 6.16925 44.271 9.78504C46.7018 13.4008 47.9994 17.6508 48 21.9983Z" fill="#212122"/>
            </svg>
            </div>
            <div className="option-text">Upload new resume</div>
          </div>
        </div>
        <div className={'application-modal-body-footer'}>
          <ButtonKMQ onClick={() => {
            setSelectedOption(null);
            onClose();
            }} type={ButtonTypes.Secondary}>
            Cancel
          </ButtonKMQ>
          <ButtonKMQ 
            onClick={handleProceed} 
            disabled={!selectedOption}
          >
            Proceed
          </ButtonKMQ>
        </div>
      </div>
    </Modal>
  );
};

export default ApplicationModal;